import React from "react";

import { formatDateOnly } from "../../../utils/datetime";
import { tenancyStatusLabelDe } from "../../../utils/tenancyStatusLabels";
import PersonCell, { EmptyPersonCell } from "./PersonCell";
import TenancyRowActions from "./TenancyRowActions";
import { formatChf, unitCountLabel } from "./tenantOverviewData";

/**
 * One unit as a card: a tinted header with the unit's name and the number of
 * tenancies under it, then those tenancies as a table on wide screens and as
 * stacked cards on narrow ones.
 *
 * Both layouts render the same fields. The mobile card is a re-flow, not a
 * reduction — dropping the status or the move-out date below a breakpoint
 * would make the page answer different questions on a phone than on a laptop.
 *
 * The status word comes from `tenancy.display_status` through
 * `adminTenancyStatusLabel` below — the neutral `tenancyStatusLabelDe` for
 * every status but one. The frontend's own `deriveTenantOperationalStatus` is
 * not used here: it collapses a cancelled tenancy into "ended" and renders it
 * as "Ausgezogen", which is the opposite of what happened.
 */

const STATUS_TONES = {
  active:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  reserved: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  notice_given:
    "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  ended: "border-border bg-muted text-muted-foreground",
  cancelled:
    "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
};

/**
 * Admin wording for one status; everything else is delegated unchanged.
 *
 * The admin overview says "Vor Einzug storniert" because that is what a
 * cancelled tenancy IS here: `cancel_tenancy` is the only writer of the status
 * and it refuses once a move-in has been documented, so the row is always a
 * tenancy nobody moved into. Production agrees — all seven cancelled tenancies
 * were cancelled before their planned move-in, none carries move-in evidence,
 * and none carries a termination or an actual move-out.
 *
 * `tenancyStatusLabelDe` keeps saying "Storniert" on purpose. It is the label
 * the TENANT and the LANDLORD portals render, and "vor Einzug" is admin framing
 * — a tenant reading their own contract list has no use for it. That is why
 * this is a local override and not an edit to the shared helper.
 *
 * Delegation instead of a second full table: two copies would drift the first
 * time a status is added to one of them.
 */
export function adminTenancyStatusLabel(status) {
  if (status === "cancelled") return "Vor Einzug storniert";
  return tenancyStatusLabelDe(status);
}

export function StatusPill({ status }) {
  const tone = STATUS_TONES[status] || "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-[9px] py-[2px] text-[11px] font-medium ${tone}`}
    >
      {adminTenancyStatusLabel(status)}
    </span>
  );
}

/**
 * The status of one tenancy, which is sometimes two facts.
 *
 * A contract terminated for a date that has not arrived is still running: the
 * tenant lives there, the rent is still billed, and it is still the admin's
 * problem. Its badge says "Gekündigt" — that is the state it is IN — but a row
 * showing only that reads as finished, and the same row is returned by the
 * "Aktiv" filter. So it shows both, and neither word is invented: they are the
 * two existing pills, in the two tones the file already defines, and both are
 * words that exist in the toolbar so a reader can map a badge back to a filter.
 *
 * Deliberately NOT one combined "Aktiv · Gekündigt" pill: that is a sixth
 * status vocabulary word which no filter offers.
 *
 * `layout` picks how the pair sits, and the default is the table case:
 * "stacked", because a status cell shares its row with cells that are already
 * two lines — a second line costs nothing there, while a second pill on the
 * same line widens a column in an eight-column table. "inline" is for the
 * detail header, where the pill sits on a wrapping line beside a heading and a
 * stacked column would misalign it. Same two pills either way; only the
 * direction differs, so neither surface can end up saying something different.
 *
 * `isRunning` comes from the server (`tenancy_is_running_on`). Nothing here
 * looks at a date — see the note in `buildTenancyRow`.
 */
export function TenancyStatusCell({ displayStatus, isRunning, layout = "stacked" }) {
  const alsoRunning = displayStatus === "notice_given" && isRunning === true;
  if (!alsoRunning) return <StatusPill status={displayStatus} />;
  return (
    <span
      className={
        layout === "inline"
          ? "inline-flex flex-wrap items-center gap-[4px]"
          : "inline-flex flex-col items-start gap-[3px]"
      }
    >
      <StatusPill status="active" />
      <StatusPill status={displayStatus} />
    </span>
  );
}

/** Room name over its area — the area line is omitted when unknown. */
function TenancyCell({ row }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[12.5px] text-foreground">
        {row.roomName || "—"}
      </div>
      <div className="text-[11px] text-muted-foreground">
        {row.roomSizeM2 == null ? "—" : `${row.roomSizeM2} m²`}
      </div>
    </div>
  );
}

function DatesCell({ row }) {
  return (
    <div className="whitespace-nowrap">
      <div className="text-[12.5px] tabular-nums text-foreground">
        {formatDateOnly(row.moveInDate)}
      </div>
      <div className="text-[11px] tabular-nums text-muted-foreground">
        {formatDateOnly(row.displayEndDate)}
      </div>
    </div>
  );
}

/**
 * Monthly revenue equivalent — never labelled as rent. `null` means the value
 * would only have been the status-driven zero, so the cell shows an em dash.
 */
function RevenueCell({ row }) {
  return (
    <div className="whitespace-nowrap text-[12.5px] font-medium tabular-nums text-foreground">
      {row.revenueEquivalent == null ? "—" : formatChf(row.revenueEquivalent)}
    </div>
  );
}

function CoTenantCell({ row, onOpenPerson }) {
  if (!row.coTenants || row.coTenants.length === 0) return <EmptyPersonCell />;
  return (
    <PersonCell
      person={row.coTenants[0]}
      extraCount={row.coTenants.length - 1}
      onOpen={onOpenPerson}
    />
  );
}

export default function TenantUnitGroup({
  group,
  collapsed,
  onToggle,
  paginated,
  onOpenTenant,
  onOpenPerson,
  buildActions,
}) {
  const count = group.rows.length;
  const headingId = `unit-group-${group.unitId || "none"}`;

  return (
    <section className="overflow-hidden rounded-[10px] border border-border bg-card">
      <h3 className="m-0">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls={`${headingId}-panel`}
          className="flex w-full items-center gap-[10px] border-b border-border bg-muted/40 px-[14px] py-[10px] text-left hover:bg-muted/70 focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span
            aria-hidden="true"
            className={`text-[10px] text-muted-foreground transition-transform ${
              collapsed ? "" : "rotate-90"
            }`}
          >
            ▶
          </span>
          <span className="truncate text-[14px] font-semibold text-foreground">
            {group.title}
          </span>
          {group.subtitle ? (
            <span className="truncate text-[12px] text-muted-foreground">
              {group.subtitle}
            </span>
          ) : null}
          <span className="ml-auto whitespace-nowrap rounded-full border border-border bg-card px-[9px] py-[2px] text-[11px] text-muted-foreground">
            {unitCountLabel(count, { paginated })}
          </span>
        </button>
      </h3>

      {collapsed ? null : (
        <div id={`${headingId}-panel`}>
          {/* Desktop: the full table. */}
          <div className="hidden overflow-x-auto lg:block" data-testid="unit-table">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  {[
                    "Hauptmieter",
                    "Co-Mieter",
                    "Mietverhältnis",
                    "Einzug / Auszug",
                    "Monatl. Ertrag",
                    "Status",
                    "",
                  ].map((h, i) => (
                    <th
                      key={h || `actions-${i}`}
                      scope="col"
                      className="whitespace-nowrap px-[14px] py-[8px] text-[11px] font-medium text-muted-foreground"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => onOpenTenant(row)}
                    className="cursor-pointer border-b border-border/70 last:border-b-0 hover:bg-muted/50"
                  >
                    <td className="px-[14px] py-[10px] align-top">
                      <PersonCell person={row.primary} onOpen={onOpenPerson} />
                    </td>
                    <td className="px-[14px] py-[10px] align-top">
                      <CoTenantCell row={row} onOpenPerson={onOpenPerson} />
                    </td>
                    <td className="px-[14px] py-[10px] align-top">
                      <TenancyCell row={row} />
                    </td>
                    <td className="px-[14px] py-[10px] align-top">
                      <DatesCell row={row} />
                    </td>
                    <td className="px-[14px] py-[10px] align-top">
                      <RevenueCell row={row} />
                    </td>
                    <td className="px-[14px] py-[10px] align-top">
                      <TenancyStatusCell
                        displayStatus={row.displayStatus}
                        isRunning={row.isRunning}
                      />
                    </td>
                    <td className="px-[14px] py-[10px] align-top text-right">
                      <TenancyRowActions items={buildActions(row)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Narrow screens: one card per tenancy, same fields. */}
          <div className="flex flex-col lg:hidden" data-testid="unit-cards">
            {group.rows.map((row) => (
              <div
                key={row.id}
                onClick={() => onOpenTenant(row)}
                className="cursor-pointer border-b border-border/70 px-[14px] py-[12px] last:border-b-0 hover:bg-muted/50"
              >
                <div className="flex items-start justify-between gap-2">
                  <PersonCell person={row.primary} onOpen={onOpenPerson} />
                  <div className="flex items-start gap-1">
                    <TenancyStatusCell
                      displayStatus={row.displayStatus}
                      isRunning={row.isRunning}
                    />
                    <TenancyRowActions items={buildActions(row)} />
                  </div>
                </div>

                <div className="mt-[10px] grid grid-cols-2 gap-x-3 gap-y-[10px] pl-[42px] sm:grid-cols-3">
                  <div>
                    <p className="m-0 text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                      Co-Mieter
                    </p>
                    <div className="mt-[4px]">
                      <CoTenantCell row={row} onOpenPerson={onOpenPerson} />
                    </div>
                  </div>
                  <div>
                    <p className="m-0 text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                      Mietverhältnis
                    </p>
                    <div className="mt-[4px]">
                      <TenancyCell row={row} />
                    </div>
                  </div>
                  <div>
                    <p className="m-0 text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                      Einzug / Auszug
                    </p>
                    <div className="mt-[4px]">
                      <DatesCell row={row} />
                    </div>
                  </div>
                  <div>
                    <p className="m-0 text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                      Monatl. Ertrag
                    </p>
                    <div className="mt-[4px]">
                      <RevenueCell row={row} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
